// Deal Pipeline spec §3.1–§3.3 — TypeScript types mirroring the counterparty
// DTOs. Source of truth lives in
//   src/PhoenixCapital.Core/DTOs/Counterparties/CounterpartyDtos.cs
//   src/PhoenixCapital.Core/Enums/CounterpartyEnums.cs
// When the backend changes those, this file must be updated in lockstep.

// ============== Enums (string unions, matching enum names) ==========

export type CounterpartyStatus = "Active" | "Archived";

export type EntityTypeCode =
  | "LLC" | "SCorp" | "CCorp" | "LP" | "LLP" | "Trust" | "SoleProp" | "Other";

export type EntityFormationStatus =
  | "PendingFormation" | "Formed" | "InGoodStanding" | "Dissolved" | "Other";

export type PreliminaryCreditScoreSource =
  | "BrokerReported" | "SelfReported" | "PulledByPhoenix" | "Other";

export type AccreditedInvestorStatus =
  | "Verified" | "SelfDeclared" | "NotApplicable" | "Unknown";

export type AccreditedInvestorBasis =
  | "IncomeTest" | "NetWorthTest" | "EntityTest" | "Professional" | "Other";

export type CounterpartyTaxFilingStatus =
  | "ScheduleC" | "LLCDisregarded" | "LLCPartnership" | "SCorp" | "CCorp" | "Other";

export type LeaseOrOwn = "Lease" | "Own" | "Mixed";

export type MaritalStatus = "Single" | "Married" | "Divorced" | "Widowed" | "Other";

export type USCitizenStatus =
  | "Citizen" | "PermanentResident" | "WorkAuthorization" | "Other";

export type PreferredContactMethod = "Email" | "Phone" | "Either";

export type PrimaryPhoneType = "Mobile" | "Work" | "Home" | "Other";

export type BankAccountType = "Checking" | "Savings" | "Other";

export type DedupMatchStrength = "None" | "Possible" | "Likely" | "Strong";

// ============== Shared ==============================================

export interface AddressDto {
  streetAddress1: string;
  streetAddress2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export const EMPTY_ADDRESS: AddressDto = {
  streetAddress1: "",
  streetAddress2: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

export interface DedupCandidateDto {
  id: string;
  displayName: string;
  matchStrength: DedupMatchStrength;
  matchReason: string;
}

export interface DedupCheckResultDto {
  strength: DedupMatchStrength;
  candidates: DedupCandidateDto[];
}

// ============== Person ==============================================

export interface PersonListItemDto {
  id: string;
  displayName: string;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  state?: string | null;
  status: CounterpartyStatus;
  createdAt: string;
}

export interface PersonDetailDto {
  id: string;
  status: CounterpartyStatus;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  displayName: string;
  dateOfBirth?: string | null;
  ssnMasked?: string | null;
  usCitizen?: USCitizenStatus | null;
  maritalStatus?: MaritalStatus | null;
  primaryEmail: string;
  secondaryEmail?: string | null;
  primaryPhone: string;
  primaryPhoneType?: PrimaryPhoneType | null;
  secondaryPhone?: string | null;
  preferredContactMethod?: PreferredContactMethod | null;
  address: AddressDto;
  mailingAddress?: AddressDto | null;
  industryExperienceYears?: number | null;
  estimatedNetWorth?: number | null;
  estimatedLiquidity?: number | null;
  preliminaryCreditScore?: number | null;
  preliminaryCreditScoreSource?: PreliminaryCreditScoreSource | null;
  equityInjectionSource?: string | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumberMasked?: string | null;
  bankAccountNumberMasked?: string | null;
  bankAccountType?: BankAccountType | null;
  tags: string[];
  createdAt: string;
  modifiedAt?: string | null;
}

export interface CreatePersonRequest {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  dateOfBirth?: string | null;
  ssn?: string | null;
  usCitizen?: USCitizenStatus | null;
  maritalStatus?: MaritalStatus | null;
  primaryEmail: string;
  secondaryEmail?: string | null;
  primaryPhone: string;
  primaryPhoneType?: PrimaryPhoneType | null;
  secondaryPhone?: string | null;
  preferredContactMethod?: PreferredContactMethod | null;
  address: AddressDto;
  mailingAddress?: AddressDto | null;
  industryExperienceYears?: number | null;
  estimatedNetWorth?: number | null;
  estimatedLiquidity?: number | null;
  preliminaryCreditScore?: number | null;
  preliminaryCreditScoreSource?: PreliminaryCreditScoreSource | null;
  equityInjectionSource?: string | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: BankAccountType | null;
  tags?: string[] | null;
  confirmCreateNew?: boolean;
  createNewReason?: string | null;
}

export interface UpdatePersonRequest extends Omit<CreatePersonRequest, "confirmCreateNew" | "createNewReason"> {
  status: CounterpartyStatus;
}

export interface PersonDedupCheckRequest {
  firstName?: string | null;
  lastName?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  state?: string | null;
  dateOfBirth?: string | null;
  ssn?: string | null;
}

// ============== Entity (non-operating legal entity) =================

export interface EntityListItemDto {
  id: string;
  legalEntityName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation?: string | null;
  status: CounterpartyStatus;
  createdAt: string;
}

export interface EntityDetailDto {
  id: string;
  status: CounterpartyStatus;
  legalEntityName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  einMasked?: string | null;
  formationStatus?: EntityFormationStatus | null;
  primaryEmail: string;
  primaryPhone: string;
  address: AddressDto;
  mailingAddress?: AddressDto | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumberMasked?: string | null;
  bankAccountNumberMasked?: string | null;
  bankAccountType?: BankAccountType | null;
  authorizedSignersJson?: string | null;
  tags: string[];
  createdAt: string;
  modifiedAt?: string | null;
}

export interface AuthorizedSigner {
  personId: string;
  title?: string | null;
  scope?: string | null;
}

export interface CreateEntityRequest {
  legalEntityName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  ein?: string | null;
  formationStatus?: EntityFormationStatus | null;
  primaryEmail: string;
  primaryPhone: string;
  address: AddressDto;
  mailingAddress?: AddressDto | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: BankAccountType | null;
  authorizedSignersJson?: string | null;
  tags?: string[] | null;
  confirmCreateNew?: boolean;
  createNewReason?: string | null;
}

export interface UpdateEntityRequest extends Omit<CreateEntityRequest, "confirmCreateNew" | "createNewReason"> {
  status: CounterpartyStatus;
}

export interface EntityDedupCheckRequest {
  legalEntityName?: string | null;
  dba?: string | null;
  stateOfFormation?: string | null;
  ein?: string | null;
}

// ============== Business (operating business) =======================

export interface BusinessListItemDto {
  id: string;
  legalEntityName: string;
  dba?: string | null;
  naicsCode?: string | null;
  hqCity?: string | null;
  hqState?: string | null;
  status: CounterpartyStatus;
  createdAt: string;
}

export interface BusinessDetailDto {
  id: string;
  status: CounterpartyStatus;
  legalEntityName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation: string;
  dateOfFormation?: string | null;
  einMasked?: string | null;
  naicsCode: string;
  industryDescription: string;
  /** Derived from dateOfFormation. */
  yearsOperating?: number | null;
  /** Deal Submission spec §6 — broker-reported years in
   *  operation, distinct from the derived yearsOperating. */
  yearsInOperation?: number | null;
  numberOfLocations?: number | null;
  numberOfEmployees?: number | null;
  annualRevenuePreliminary?: number | null;
  annualEBITDAPreliminary?: number | null;
  recurringRevenuePct?: number | null;
  customerConcentrationTop5Pct?: number | null;
  leaseOrOwn?: LeaseOrOwn | null;
  leaseExpirationDate?: string | null;
  hqAddress: AddressDto;
  additionalLocationsJson?: string | null;
  website?: string | null;
  briefDescription?: string | null;
  tags: string[];
  createdAt: string;
  modifiedAt?: string | null;
}

export interface CreateBusinessRequest {
  legalEntityName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation: string;
  dateOfFormation?: string | null;
  ein?: string | null;
  naicsCode: string;
  industryDescription: string;
  /** Deal Submission spec §6 — broker-reported years in
   *  operation. Optional/additive. */
  yearsInOperation?: number | null;
  numberOfLocations?: number | null;
  numberOfEmployees?: number | null;
  annualRevenuePreliminary?: number | null;
  annualEBITDAPreliminary?: number | null;
  recurringRevenuePct?: number | null;
  customerConcentrationTop5Pct?: number | null;
  leaseOrOwn?: LeaseOrOwn | null;
  leaseExpirationDate?: string | null;
  hqAddress: AddressDto;
  additionalLocationsJson?: string | null;
  website?: string | null;
  briefDescription?: string | null;
  tags?: string[] | null;
  confirmCreateNew?: boolean;
  createNewReason?: string | null;
}

export interface UpdateBusinessRequest extends Omit<CreateBusinessRequest, "confirmCreateNew" | "createNewReason"> {
  status: CounterpartyStatus;
}

export interface BusinessDedupCheckRequest {
  legalEntityName?: string | null;
  dba?: string | null;
  stateOfFormation?: string | null;
  ein?: string | null;
  naicsCode?: string | null;
  hqCity?: string | null;
}

// ============== Permission keys (single source) =====================

export const COUNTERPARTY_PERMISSIONS = {
  Person: {
    Read: "Persons.Read",
    Create: "Persons.Create",
    Update: "Persons.Update",
    Delete: "Persons.Delete",
  },
  Entity: {
    Read: "Entities.Read",
    Create: "Entities.Create",
    Update: "Entities.Update",
    Delete: "Entities.Delete",
  },
  Business: {
    Read: "Businesses.Read",
    Create: "Businesses.Create",
    Update: "Businesses.Update",
    Delete: "Businesses.Delete",
  },
} as const;

export type CounterpartyKind = "Person" | "Entity" | "Business";
